import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { motion } from "framer-motion";
import { Download, Search, Filter } from "lucide-react";
import type { ProductItem } from "../api/types";

export default function Products() {
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const toTitleCase = (value: string) =>
    value
      .replace(/-/g, " ")
      .split(" ")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  useEffect(() => {
    api
      .products()
      .then((data: ProductItem[]) => {
        const normalized: ProductItem[] = data.map((p) => ({
          ...p,
          category: p.category ?? "journal-pages",
        }));
        setProducts(normalized);
      })
      .catch(console.error);
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesCategory = selectedCategory === "all" || product.category === selectedCategory;
      const haystack = `${product.title ?? ""} ${product.description ?? ""}`.toLowerCase();
      const matchesSearch = haystack.includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [products, selectedCategory, searchQuery]);

  const availableCategories = useMemo(() => {
    const set = new Set<string>();
    products.forEach((p) => {
      if (p.category) set.add(p.category);
    });
    return Array.from(set);
  }, [products]);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#D4A5A5]/20 to-[#FDF8F5] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="font-serif text-5xl sm:text-6xl font-semibold text-[#3D3D3D] mb-4">Shop</h1>
            <p className="font-sans text-lg text-[#3D3D3D]/70 max-w-2xl mx-auto">
              Discover beautiful, hand-crafted journaling pages and resources to enhance your time in Scripture
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-12">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-3 rounded-full border border-gray-200 font-sans focus:border-[#D4A5A5] focus:ring-2 focus:ring-[#D4A5A5] outline-none w-full"
            />
          </div>

          {/* Category Filters - show only categories with items */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`rounded-full font-sans px-4 py-2 border transition-colors ${
                selectedCategory === "all"
                  ? "bg-[#3D3D3D] text-white border-transparent"
                  : "border-gray-200 text-[#3D3D3D] hover:border-[#D4A5A5] hover:text-[#D4A5A5]"
              }`}
            >
              All Products
            </button>
            {availableCategories.map((value) => (
              <button
                key={value}
                onClick={() => setSelectedCategory(value)}
                className={`rounded-full font-sans px-4 py-2 border transition-colors ${
                  selectedCategory === value
                    ? "bg-[#3D3D3D] text-white border-transparent"
                    : "border-gray-200 text-[#3D3D3D] hover:border-[#D4A5A5] hover:text-[#D4A5A5]"
                }`}
              >
                {toTitleCase(value)}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {products.length === 0 ? (
          <div className="text-center py-20">
            <Filter className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-serif text-2xl text-[#3D3D3D] mb-2">No products found</h3>
            <p className="font-sans text-[#3D3D3D]/60">Try adjusting your filters or search query</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {filteredProducts.map((product, index) => (
              <motion.div key={product.squareProductId} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.05 }}>
                <Link to={`/products/${product.squareProductId}`} state={{ product }}>
                  <div className="group cursor-pointer">
                    <div className="relative overflow-hidden rounded-2xl bg-[#FDF8F5] mb-4">
                      <img
                        src={product.imageUrls?.[0]}
                        alt={product.title}
                        className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-[#3D3D3D]/0 group-hover:bg-[#3D3D3D]/20 transition-colors duration-300" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="bg-white text-[#3D3D3D] px-6 py-2 rounded-full font-sans text-sm font-medium">View Details</span>
                      </div>
                      {product.category && (
                        <div className="absolute top-4 left-4">
                          <span className="bg-white/90 backdrop-blur-sm text-[#3D3D3D] px-3 py-1 rounded-full font-sans text-xs font-medium capitalize">
                            {toTitleCase(product.category)}
                          </span>
                        </div>
                      )}
                    </div>
                    <h3 className="font-serif text-lg font-semibold text-[#3D3D3D] mb-1 group-hover:text-[#D4A5A5] transition-colors">{product.title}</h3>
                    <div className="flex items-center justify-between">
                      <span className="font-sans text-lg font-medium text-[#D4A5A5]">${product.price?.toFixed(2)}</span>
                      {product.pagesCount && (
                        <span className="font-sans text-sm text-[#3D3D3D]/50 flex items-center gap-1">
                          <Download className="w-3 h-3" />
                          {product.pagesCount} pages
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
